<template>
  <b-card-code title="">
    <div class="wp-header mb-2">
      <div>
        <h4 class="mb-25 wp-title">
          Fizikai létszám menedzsment
        </h4>
        <small class="wp-subtitle">Napi brigád és projekt hozzárendelések</small>
      </div>
      <div class="wp-actions">
        <div class="wp-date-wrap">
          <label class="mb-50 wp-label">Dátum</label>
          <input
            v-model="workDate"
            type="date"
            class="form-control wp-input"
            @change="fetchData"
          >
        </div>
        <button
          class="btn btn-primary wp-primary-btn"
          @click="openCrewModal"
        >
          Új brigád
        </button>
      </div>
    </div>

    <div
      v-if="pageMessage"
      class="alert alert-warning"
    >
      {{ pageMessage }}
    </div>

    <div class="wp-selector mb-2">
      <div class="wp-selector-field">
        <label class="wp-label">Kiosztandó brigád</label>
        <select
          v-model="selectedCrewId"
          class="form-control wp-input"
        >
          <option :value="null">
            -- Válassz brigádot --
          </option>
          <option
            v-for="crew in crews"
            :key="crew.id"
            :value="crew.id"
          >
            {{ crew.name }}
          </option>
        </select>
      </div>
      <div class="wp-selector-help">
        Kiválasztás után a projektkártyákon a "Brigád hozzárendelése" gombbal tudsz napi kiosztást menteni.
      </div>
    </div>

    <div class="project-grid">
      <article
        v-for="project in projects"
        :key="project.id"
        class="project-tile"
      >
        <div class="project-split">
          <section class="project-main">
            <header class="project-tile-head">
              <div class="project-tile-title-wrap">
                <h5 class="project-tile-title">
                  {{ project.work_number ? `${project.work_number} | ` : '' }}{{ project.name }}
                </h5>
                <small class="project-tile-meta">Folyamatban lévő projekt</small>
              </div>
              <button
                class="btn btn-sm btn-primary wp-primary-btn project-assign-btn"
                :disabled="savingAssignments"
                @click="assignSelectedCrewToProject(project.id)"
              >
                Brigád hozzárendelése
              </button>
            </header>
          </section>
          <aside class="project-crews">
            <div class="project-crews-title">
              Brigádok
            </div>
            <div class="assigned-crew-list">
              <div
                v-if="!projectCrews(project.id).length"
                class="project-empty"
              >
                Nincs brigád hozzárendelve erre a napra.
              </div>
              <div
                v-for="crewItem in projectCrews(project.id)"
                :key="`project-${project.id}-crew-${crewItem.crew_id || crewItem.crew_name}`"
                class="assigned-crew-item"
              >
                <div class="assigned-crew-details">
                  <strong>{{ crewItem.crew_name || 'Egyéni hozzárendelés' }}</strong>
                  <small>{{ crewItem.member_count }} fő</small>
                </div>
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="removeCrewFromProject(project.id, crewItem.crew_id)"
                >
                  Levétel
                </button>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </div>

    <b-modal
      id="crew-modal"
      ref="crewModal"
      title="Új brigád"
      hide-footer
    >
      <div class="form-group">
        <label>Brigád név</label>
        <input
          v-model="crewForm.name"
          type="text"
          class="form-control"
          placeholder="pl. Kányási"
        >
      </div>
      <div class="form-group">
        <label>Tagok</label>
        <select
          v-model="crewForm.member_ids"
          class="form-control member-select"
          multiple
          size="10"
        >
          <option
            v-for="employee in employees"
            :key="employee.id"
            :value="employee.id"
          >
            {{ employee.name }}
          </option>
        </select>
      </div>
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-outline-secondary mr-1"
          @click="$refs.crewModal.hide()"
        >
          Mégse
        </button>
        <button
          class="btn btn-primary"
          :disabled="savingCrew"
          @click="saveCrew"
        >
          {{ savingCrew ? 'Mentés...' : 'Brigád mentése' }}
        </button>
      </div>
    </b-modal>
  </b-card-code>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    const today = new Date()
    const month = `${today.getMonth() + 1}`.padStart(2, '0')
    const day = `${today.getDate()}`.padStart(2, '0')

    return {
      loading: false,
      savingCrew: false,
      savingAssignments: false,
      pageMessage: '',
      workDate: `${today.getFullYear()}-${month}-${day}`,
      employees: [],
      projects: [],
      crews: [],
      members: [],
      assignments: [],
      selectedCrewId: null,
      crewForm: {
        id: null,
        name: '',
        member_ids: [],
      },
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    toast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon',
          variant,
        },
      })
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    openCrewModal() {
      this.crewForm = {
        id: null,
        name: '',
        member_ids: [],
      }
      this.$refs.crewModal.show()
    },
    projectCrews(projectId) {
      const rows = this.assignments.filter(row => Number(row.project_id) === Number(projectId))
      const grouped = new Map()

      rows.forEach(row => {
        const key = row.crew_id ? `crew-${row.crew_id}` : `single-${row.employee_id}`
        if (!grouped.has(key)) {
          grouped.set(key, {
            crew_id: row.crew_id || null,
            crew_name: row.crew_name || 'Egyéni hozzárendelés',
            member_count: 0,
            assignment_ids: [],
          })
        }
        const bucket = grouped.get(key)
        bucket.member_count += 1
        bucket.assignment_ids.push(row.id)
      })

      return Array.from(grouped.values())
    },
    selectedCrewMemberIds() {
      if (!this.selectedCrewId) return []
      return this.members
        .filter(item => Number(item.crew_id) === Number(this.selectedCrewId))
        .map(item => Number(item.employee_id))
        .filter(id => id > 0)
    },
    async fetchData() {
      this.loading = true
      try {
        const response = await axios.get('workforce/planning', {
          params: { date: this.workDate },
          headers: this.authHeaders(),
        })

        this.employees = response.data.employees || []
        this.projects = response.data.projects || []
        this.crews = response.data.crews || []
        this.members = response.data.members || []
        this.assignments = response.data.assignments || []
        this.pageMessage = response.data.message || ''

        if (this.selectedCrewId) {
          const exists = this.crews.some(crew => Number(crew.id) === Number(this.selectedCrewId))
          if (!exists) this.selectedCrewId = null
        }
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült betölteni az adatokat.'
        this.pageMessage = message
        this.toast(message, 'danger')
      } finally {
        this.loading = false
      }
    },
    async saveCrew() {
      if (!this.crewForm.name.trim()) {
        this.toast('Adj meg brigád nevet.', 'danger')
        return
      }
      this.savingCrew = true
      try {
        await axios.post('workforce/crew', {
          id: this.crewForm.id,
          name: this.crewForm.name,
          member_ids: this.crewForm.member_ids,
        }, {
          headers: this.authHeaders(),
        })
        this.toast('Brigád mentve.')
        this.$refs.crewModal.hide()
        await this.fetchData()
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült menteni a brigádot.'
        this.toast(message, 'danger')
      } finally {
        this.savingCrew = false
      }
    },
    async assignSelectedCrewToProject(projectId) {
      if (!this.selectedCrewId) {
        this.toast('Válassz brigádot a felső sávban.', 'danger')
        return
      }

      const memberIds = this.selectedCrewMemberIds()
      if (!memberIds.length) {
        this.toast('A kiválasztott brigádnak nincs tagja.', 'danger')
        return
      }

      this.savingAssignments = true
      try {
        const response = await axios.post('workforce/assignments/bulk', {
          work_date: this.workDate,
          project_id: projectId,
          crew_id: this.selectedCrewId,
          employee_ids: memberIds,
        }, {
          headers: this.authHeaders(),
        })
        this.assignments = response.data.assignments || []
        this.toast(response.data.message || 'Napi hozzárendelés mentve.')
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült menteni a hozzárendelést.'
        this.toast(message, 'danger')
      } finally {
        this.savingAssignments = false
      }
    },
    async removeCrewFromProject(projectId, crewId) {
      const rows = this.assignments.filter(row => {
        const projectMatch = Number(row.project_id) === Number(projectId)
        if (!projectMatch) return false
        if (crewId === null || crewId === undefined) return !row.crew_id
        return Number(row.crew_id) === Number(crewId)
      })

      if (!rows.length) return

      try {
        await Promise.all(rows.map(row => axios.delete(`workforce/assignments/${row.id}`, {
          headers: this.authHeaders(),
        })))
        this.assignments = this.assignments.filter(row => !rows.some(rem => Number(rem.id) === Number(row.id)))
        this.toast('Brigád levéve a projektről.')
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült törölni a hozzárendelést.'
        this.toast(message, 'danger')
      }
    },
  },
}
</script>

<style scoped>
.gap-1 {
  gap: 12px;
}

.wp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.wp-title {
  color: #f1f4ff;
}

.wp-subtitle {
  color: #8f98bf;
}

.wp-actions {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.wp-date-wrap {
  min-width: 220px;
}

.wp-label {
  color: #c3c9e8;
  font-weight: 600;
}

.wp-input {
  border: 1px solid #394264;
  background: #1f2747;
  color: #f2f5ff;
}

.wp-input:focus {
  border-color: #628ef5;
  box-shadow: 0 0 0 2px rgba(98, 142, 245, 0.2);
}

.wp-primary-btn {
  background: #4f88eb;
  border-color: #4f88eb;
  font-weight: 600;
}

.wp-primary-btn:hover {
  background: #4477cf;
  border-color: #4477cf;
}

.wp-selector {
  display: grid;
  grid-template-columns: minmax(260px, 430px) 1fr;
  gap: 14px;
  align-items: end;
  border: 1px solid #2d3657;
  border-radius: 12px;
  padding: 14px;
  background: linear-gradient(180deg, rgba(40, 49, 81, 0.45), rgba(24, 31, 55, 0.45));
}

.wp-selector-help {
  color: #96a0c8;
  line-height: 1.4;
}

.project-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.project-tile {
  margin: 0;
  border: 1px solid #2e385a;
  border-radius: 14px;
  padding: 14px;
  background: linear-gradient(180deg, rgba(32, 41, 71, 0.72), rgba(22, 30, 54, 0.72));
  box-shadow: inset 3px 0 0 #5f8fe8;
}

.project-split {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
  align-items: stretch;
}

.project-main {
  min-width: 0;
}

.project-crews {
  min-width: 0;
  border-left: 1px solid #394264;
  padding-left: 14px;
}

.project-crews-title {
  color: #b8c2e9;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.project-tile-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.project-tile-title-wrap {
  min-width: 0;
}

.project-tile-title {
  color: #e8ecff;
  margin: 0;
  line-height: 1.25;
}

.project-tile-meta {
  color: #909abd;
}

.project-assign-btn {
  white-space: nowrap;
}

.assigned-crew-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.project-empty {
  color: #8f99bc;
  padding-top: 4px;
}

.assigned-crew-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #3a4568;
  border-radius: 10px;
  padding: 9px 11px;
  background: rgba(42, 53, 88, 0.45);
}

.assigned-crew-details {
  display: flex;
  flex-direction: column;
}

.assigned-crew-details strong {
  color: #f2f5ff;
}

.assigned-crew-details small {
  color: #96a0c6;
}

.member-select {
  min-height: 260px;
}

@media (max-width: 900px) {
  .wp-selector {
    grid-template-columns: 1fr;
  }

  .project-split {
    grid-template-columns: 1fr;
  }

  .project-crews {
    border-left: 0;
    border-top: 1px solid #394264;
    padding-left: 0;
    padding-top: 12px;
  }

  .project-tile-head {
    flex-direction: column;
  }
}
</style>
